// BotaoParticulas.js — Botão que solta partículas ao ser clicado.
//
// Adaptado do ParticleButton (21st.dev). Diferenças em relação ao original,
// todas por causa de ONDE ele é usado — o botão de finalizar a compra:
//
//  1. O original ignora o `onClick` recebido: define `handleClick` e nunca o
//     chama. Num botão de demonstração passa despercebido; no botão que fecha
//     o pedido, isso significaria o clique não fazer nada. Aqui o onClick é
//     sempre chamado.
//
//  2. O original solta as partículas em TODO clique. Aqui elas só saem quando
//     o clique realmente vale — se o botão está desabilitado ou processando,
//     não há comemoração. Festa em cima de um clique que não fez nada é pior
//     do que nenhuma festa.
//
//  3. As partículas são posicionadas em relação à VIEWPORT (`fixed`) usando a
//     posição do botão no momento do clique, como no original, mas com
//     `pointer-events: none` e `aria-hidden` para não atrapalhar o formulário.
import React, { useCallback, useRef, useState } from "react";
import { m, AnimatePresence, useReducedMotion } from "motion/react";

const QUANTIDADE = 10;
const DURACAO = 900; // ms até limpar as partículas

const Particulas = ({ origem }) => {
  if (!origem) return null;

  return (
    <AnimatePresence>
      {Array.from({ length: QUANTIDADE }).map((_, i) => {
        // Leque para cima: ângulos entre -160° e -20°, distância variável.
        const angulo = (-160 + (140 * i) / (QUANTIDADE - 1)) * (Math.PI / 180);
        const distancia = 42 + ((i * 37) % 34);
        return (
          <m.span
            key={i}
            aria-hidden="true"
            className="fixed w-1.5 h-1.5 rounded-full bg-insigne-orange pointer-events-none"
            style={{ left: origem.x, top: origem.y, zIndex: 2147483001 }}
            initial={{ scale: 0, x: 0, y: 0, opacity: 1 }}
            animate={{
              scale: [0, 1, 0],
              x: Math.cos(angulo) * distancia,
              y: Math.sin(angulo) * distancia,
              opacity: [1, 1, 0],
            }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.65, delay: i * 0.02, ease: "easeOut" }}
          />
        );
      })}
    </AnimatePresence>
  );
};

const BotaoParticulas = ({ children, onClick, className = "", disabled, ...rest }) => {
  const ref = useRef(null);
  const [origem, setOrigem] = useState(null);
  const reduzirMovimento = useReducedMotion();

  const aoClicar = useCallback(
    (e) => {
      // Só comemora clique que vale: desabilitado não solta partícula.
      if (!disabled && !reduzirMovimento) {
        const r = ref.current?.getBoundingClientRect();
        if (r) {
          setOrigem({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
          setTimeout(() => setOrigem(null), DURACAO);
        }
      }
      // O clique de verdade acontece SEMPRE — é ele que fecha o pedido.
      if (onClick) onClick(e);
    },
    [onClick, disabled, reduzirMovimento]
  );

  return (
    <>
      <Particulas origem={origem} />
      <button
        ref={ref}
        onClick={aoClicar}
        disabled={disabled}
        className={
          className +
          " transition-transform duration-100 " +
          (origem ? "scale-[0.97]" : "")
        }
        {...rest}
      >
        {children}
      </button>
    </>
  );
};

export default BotaoParticulas;
